// frontend/src/services/adminApi.js
import axios from 'axios';
import api from './api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Main admin API instance with auth interceptor
const adminApi = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests - THIS IS THE KEY
adminApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    console.log('🔑 Token from localStorage:', token ? 'Found' : 'Not found');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        console.log('✅ Token added to request headers');
    } else {
        console.warn('⚠️ No token found in localStorage');
    }
    console.log(`📡 API Request: ${config.method.toUpperCase()} ${config.url}`);
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Handle responses
adminApi.interceptors.response.use(
    (response) => {
        console.log(`📥 API Response: ${response.status} - ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error('❌ API Error:', error.response?.status, error.response?.data);
        if (error.response?.status === 401) {
            console.log('🔒 Token expired or invalid, clearing storage');
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            if (!window.location.pathname.includes('/admin/login')) {
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(error);
    }
);

// ============================================
// AUTH
// ============================================
export const adminLogin = (email, password) => {
    console.log('🔐 Calling admin login API');
    return adminApi.post('/admin/login', { email, password });
};

// ============================================
// DASHBOARD
// ============================================
export const getDashboardStats = (organizationId) =>
    adminApi.get('/admin/dashboard/stats', { params: { organization_id: organizationId } });

// ============================================
// CANDIDATES
// ============================================
export const getCandidates = (params) => adminApi.get('/admin/candidates', { params });
export const getCandidateDetails = (id) => adminApi.get(`/admin/candidates/${id}`);
export const downloadReport = (id) => adminApi.get(`/admin/candidates/${id}/report`, { responseType: 'blob' });
export const sendReminder = (sessionId) => adminApi.post(`/email/reminder/${sessionId}`);

// ============================================
// BULK INVITE
// ============================================
export const bulkInvite = (formData) => {
    // Use adminApi here too for auth
    return adminApi.post('/admin/bulk-invite', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// ============================================
// ORGANIZATIONS
// ============================================
export const getOrganizations = () => adminApi.get('/admin/organizations');
export const createOrganization = (data) => adminApi.post('/admin/organizations', data);

// ============================================
// REPORTS
// ============================================
export const getAggregateReport = (assessmentId) => adminApi.get(`/reports/aggregate/${assessmentId}`);

// ============================================
// AUDIT LOGS
// ============================================
export const getAuditLogs = (params) => adminApi.get('/admin/audit-logs', { params });

// ============================================
// ASSESSMENT MANAGEMENT
// ============================================
export const getAssessments = (params = {}) => {
    console.log('📋 getAssessments - Params:', params);
    return adminApi.get('/assessments', { params });
};
export const getAssessment = (id) => adminApi.get(`/assessments/${id}`);
export const createAssessment = (data) => adminApi.post('/assessments', data);
export const updateAssessment = (assessmentId, data) => adminApi.post(`/assessments`, data);
export const deleteAssessment = (assessmentId) => adminApi.delete(`/assessments`);
export const getAssessmentStats = (assessmentId) => adminApi.get(`/assessments/${assessmentId}/stats`);

// ============================================
// CANDIDATE MANAGEMENT
// ============================================
export const getCandidate = (id) => adminApi.get(`/admin/candidates/${id}`);

export const exportCandidates = (params = {}) => {
    console.log('📊 Exporting candidates with params:', params);
    return adminApi.get('/admin/candidates/export', {
        params,
        responseType: 'blob',
        headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
    });
};

export const sendStandardLinkEmail = (data) => {
    console.log('📧 sendStandardLinkEmail - Data:', data);
    return adminApi.post('/admin/send-standard-link-email', data);
};

export const resendInvitation = (id) => adminApi.post(`/admin/candidates/${id}/resend`);
export const bulkResendInvitations = (ids) => adminApi.post('/admin/candidates/bulk-resend', { ids });
export const deleteCandidate = (id) => adminApi.delete(`/admin/candidates/${id}`);

// ============================================
// GENERATE STANDARD LINKS - FIXED
// ============================================
export const generateStandardLinks = (data) => {
    console.log('🔗 Generating standard link with data:', data);
    console.log('🔑 Using adminApi with auth interceptor');
    return adminApi.post('/admin/generate-standard-links', data);
};

// ============================================
// EMAIL INVITES - FIXED
// ============================================
export const sendEmailInvites = (data) => {
    console.log('📧 Sending email invites with data:', data);
    return adminApi.post('/admin/send-email-invites', data);
};

export const sendSingleEmailInvite = (data) => {
    console.log('📧 Sending single email invite with data:', data);
    return adminApi.post('/admin/send-single-email-invite', data);
};

// ============================================
// GET DEPARTMENTS
// ============================================
export const getDepartments = (assessmentId) => {
    return adminApi.get(`/admin/departments/${assessmentId}`);
};

// ============================================
// CREATE DEPARTMENT
// ============================================
export const createDepartment = (data) => {
    return adminApi.post('/admin/departments', data);
};

// ============================================
// UPDATE DEPARTMENT
// ============================================
export const updateDepartment = (id, data) => {
    return adminApi.put(`/admin/departments/${id}`, data);
};

// ============================================
// DELETE DEPARTMENT
// ============================================
export const deleteDepartment = (id) => {
    return adminApi.delete(`/admin/departments/${id}`);
};

// ============================================
// IMPORT ASSESSMENT
// ============================================
export const importAssessmentExcel = (formData) => {
    return adminApi.post('/admin/import/assessment', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

export const getImportStatus = (importId) => {
    return adminApi.get(`/admin/import/status/${importId}`);
};

export const getImportHistory = () => {
    return adminApi.get('/admin/import/history');
};

// ============================================
// FACE PHOTO APIS
// ============================================
export const getFacePhotos = (accessToken) => {
    // This uses the main api instance (not adminApi) since it's for candidate access
    return api.get(`/face-photos/${accessToken}/photos`);
};

export const getFacePhotoStatus = (accessToken) => {
    return api.get(`/face-photos/${accessToken}/status`);
};

export const getCompletedReports = (params = {}) => {
    return adminApi.get('/admin/reports', { params });
};

export const getCandidatesFiltered = (params = {}) => {
    return adminApi.get('/admin/candidates', { params });
};

export const getAssignedAssessments = () => {
    return adminApi.get('/admin/reports/assessments');
};

// ============================================
// EXPORT ALL FUNCTIONS
// ============================================
export default {
    adminLogin,
    getDashboardStats,
    getCandidates,
    getCandidate,
    getCandidateDetails,
    downloadReport,
    sendReminder,
    bulkInvite,
    getOrganizations,
    createOrganization,
    getAssessments,
    getAssessment,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    getAssessmentStats,
    getAggregateReport,
    getAuditLogs,
    exportCandidates,
    resendInvitation,
    bulkResendInvitations,
    deleteCandidate,
    generateStandardLinks,
    sendEmailInvites,
    sendSingleEmailInvite,
    sendStandardLinkEmail,
    getDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    importAssessmentExcel,
    getImportStatus,
    getImportHistory,
    getFacePhotos,
    getFacePhotoStatus,
    getCompletedReports,
    getAssignedAssessments,
    getCandidatesFiltered
};